// src/pages/laporan/LaporanDetail.tsx - Redesigned contextual sidebar
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Send, MessageSquare, Paperclip, Clock, User, Building2,
  Target, AlertTriangle, FileText, Download, TrendingUp, Tag,
  ArrowUpRight, Trash2, Calendar, ChevronRight, Info, CheckCircle,
  Play, XCircle, Pause, Award, ExternalLink, ChevronDown, Zap,
  BarChart3, Shield, Timer, CalendarCheck, UserCheck, Flag, Eye,
  Presentation, FileImage, FileSpreadsheet, Archive, File,
} from 'lucide-react';
import { Button, Card, Breadcrumb, ProgressBar, Loading } from '../../components/ui';
import Badge, { getStatusConfig } from '../../components/ui/Badge';
import { getStatusExplanation, getNextActor } from '../../utils/statusHelpers';
import { StatusProgress, WorkflowTimeline, WorkflowSummaryCard, WorkflowStatusProgress, UnifiedWorkflowActions } from '../../components/workflow';
import { LaporanMasalah, Comment } from '../../types';
import { formatDistanceToNow, differenceInDays } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';
import toast from 'react-hot-toast';
import { laporanService } from '../../services/laporan';
import { useWorkflow } from '../../hooks/useWorkflow';
import { useRole } from '../../hooks/useRole';
import { useAuthStore } from '../../store/authStore';
import { useWorkflowValidation } from '../../hooks/useWorkflowValidation';
import { useCapabilities } from '../../hooks/useCapabilities';

// ========== TYPES ==========
type LaporanPhase = 'pra_rapim' | 'pasca_rapim' | 'selesai';

// ========== HELPER: Determine Phase ==========
const getLaporanPhase = (status: string): LaporanPhase => {
  const praRapimStatuses = ['draf', 'draft', 'diajukan', 'validasi_l1', 'validasi_l2',
    'antrean_rapim', 'ditolak_l1', 'ditolak_l2'];
  const pascaRapimStatuses = ['diputuskan', 'berlangsung'];

  const selesaiStatuses = ['selesai', 'diarsipkan', 'dibatalkan', 'diselesaikan_lokal'];

  if (selesaiStatuses.includes(status)) return 'selesai';
  if (pascaRapimStatuses.includes(status)) return 'pasca_rapim';
  return 'pra_rapim';
};

const formatResolutionLevel = (level?: string | null): string => {
  switch ((level || '').toLowerCase()) {
    case 'fakultas':
      return 'Fakultas';
    case 'lembaga':
      return 'Lembaga';
    case 'warek':
      return 'Wakil Rektor';
    case 'universitas':
      return 'Universitas';
    default:
      return 'Internal';
  }
};

const formatWorkflowActorLabel = (roleName?: string | null, fallback?: string | null): string => {
  const role = (roleName || '').toLowerCase();

  const roleLabels: Record<string, string> = {
    dekan: 'Dekan',
    kalem: 'Ketua Lembaga',
    rektor: 'Rektor',
    wakil_rektor_1: 'Wakil Rektor 1',
    wakil_rektor_2: 'Wakil Rektor 2',
    wakil_rektor_3: 'Wakil Rektor 3',
    wakil_rektor_4: 'Wakil Rektor 4',
  };

  if (roleLabels[role]) {
    return roleLabels[role];
  }

  if (fallback && fallback.trim() !== '') {
    return fallback;
  }

  if (!role) {
    return 'Validator';
  }

  return role
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase());
};

const isSameHistoryMoment = (left?: any, right?: any): boolean =>
  Boolean(
    left
    && right
    && String(left.created_at || '') === String(right.created_at || '')
    && String(left.actor_id || '') === String(right.actor_id || '')
  );

// ========== SIDEBAR COMPONENTS ==========

// Compact Info Row
const InfoRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  subValue?: string;
  highlight?: boolean;
}> = ({ icon, label, value, subValue, highlight }) => (
  <div className="flex items-start gap-3">
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${highlight ? 'bg-danger/10 text-danger' : 'bg-neutral-bg text-neutral-text-secondary'
      }`}>
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[10px] uppercase tracking-wider font-semibold text-neutral-text-secondary">{label}</p>
      <p className={`text-sm font-medium ${highlight ? 'text-danger' : 'text-neutral-text-primary'}`}>{value}</p>
      {subValue && <p className="text-[10px] text-neutral-text-secondary mt-0.5">{subValue}</p>}
    </div>
  </div>
);

// Phase Badge
const PhaseBadge: React.FC<{ phase: LaporanPhase }> = ({ phase }) => {
  const config = {
    pra_rapim: { label: 'Proses Validasi', bg: 'bg-blue-100', text: 'text-blue-700', icon: Shield },
    pasca_rapim: { label: 'Tindak Lanjut', bg: 'bg-amber-100', text: 'text-amber-700', icon: Play },
    selesai: { label: 'Selesai', bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle },
  };
  const c = config[phase];
  const Icon = c.icon;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${c.bg} ${c.text}`}>
      <Icon className="w-3.5 h-3.5" />
      {c.label}
    </span>
  );
};

// ========== PRA-RAPIM SIDEBAR ==========
const PraRapimSidebar: React.FC<{
  laporan: any;
  statusLaporan: string;
  workflowValidation: any;
  pernahDitolakAtauRevisi: boolean;
  isOverdue: boolean;
  statusBadgeLabelOverride?: string | null;
  statusExplanationOverride?: string | null;
  nextActorLabelOverride?: string | null;
  actionPanel?: React.ReactNode;
}> = ({
  laporan,
  statusLaporan,
  workflowValidation,
  pernahDitolakAtauRevisi,
  isOverdue,
  statusBadgeLabelOverride,
  statusExplanationOverride,
  nextActorLabelOverride,
  actionPanel,
}) => {
  const statusConfig = getStatusConfig(statusLaporan);

  return (
  <div className="space-y-4">
    {/* Status & Progress */}
    <Card className="!p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-bold uppercase tracking-widest text-neutral-text-secondary">Status Validasi</h3>
        <PhaseBadge phase="pra_rapim" />
      </div>

      <div className="flex items-center gap-2 mb-3">
        {statusBadgeLabelOverride ? (
          <span
            className={`inline-flex items-center gap-1.5 font-semibold rounded-full whitespace-nowrap px-2.5 py-1 text-sm ${statusConfig.bgColor} ${statusConfig.textColor}`}
          >
            {statusConfig.dotColor && (
              <span className={`w-1.5 h-1.5 rounded-full ${statusConfig.dotColor}`} />
            )}
            {statusBadgeLabelOverride}
          </span>
        ) : (
          <Badge variant="status" status={statusLaporan} size="md" showDot />
        )}
        {isOverdue && (
          <span className="text-[10px] px-2 py-0.5 bg-danger/10 text-danger rounded-full font-bold">
            Terlambat
          </span>
        )}
      </div>

      <p className="text-sm text-neutral-text-secondary leading-relaxed">
        {statusExplanationOverride || getStatusExplanation(statusLaporan, {
          pernahDitolak: pernahDitolakAtauRevisi,
          warekTujuan: laporan.warek_tujuan,
          rapat_pimpinan: laporan.rapat_pimpinan,
        })}
      </p>

      {!['draf', 'draft'].includes(statusLaporan) && (
        <div className="mt-3 pt-3 border-t border-neutral-border flex items-center gap-2 text-xs">
          <Timer className="w-3.5 h-3.5 text-neutral-text-secondary" />
          <span className="text-neutral-text-secondary">Menunggu:</span>
          <span className="font-semibold text-teal-main">
            {nextActorLabelOverride || getNextActor(statusLaporan, laporan.warek_tujuan, laporan.rapat_pimpinan)}
          </span>
        </div>
      )}
    </Card>

    {actionPanel && (
      <Card className="!p-4">
        <div className="space-y-3">
          <div>
            <h3 className="text-xs font-bold uppercase tracking-widest text-neutral-text-secondary">
              Tindakan Validasi
            </h3>
            <p className="mt-1 text-sm text-neutral-text-secondary">
              Pilih tindakan yang tersedia sesuai tahap validasi laporan ini.
            </p>
          </div>
          {actionPanel}
        </div>
      </Card>
    )}

    {/* Workflow Progress (hanya jika ada) */}
    {workflowValidation.summary?.steps?.length > 0 && (
      <Card className="!p-4">
        <h3 className="text-xs font-bold uppercase tracking-widest text-neutral-text-secondary mb-3">
          Alur Validasi
        </h3>
        <WorkflowStatusProgress
          steps={workflowValidation.summary.steps}
          currentStep={workflowValidation.summary.current_step}
          resolution={workflowValidation.summary.resolution}
          needsRapim={workflowValidation.summary.needs_rapim}
          laporanStatus={statusLaporan}

        />
      </Card>
    )}

    {/* Info Dasar */}
    <Card className="!p-4">
      <h3 className="text-xs font-bold uppercase tracking-widest text-neutral-text-secondary mb-4">Informasi</h3>
      <div className="space-y-3">
        <InfoRow
          icon={<User className="w-4 h-4" />}
          label="Pelapor"
          value={laporan.user?.name || '-'}
          subValue={laporan.user?.role?.display_name}
        />
        <InfoRow
          icon={<Building2 className="w-4 h-4" />}
          label="Unit"
          value={laporan.unit?.name || '-'}
        />
        <InfoRow
          icon={<Calendar className="w-4 h-4" />}
          label="Dibuat"
          value={laporan.created_at
            ? formatDistanceToNow(new Date(laporan.created_at), { addSuffix: true, locale: idLocale })
            : '-'
          }
        />
      </div>
    </Card>

    {/* Skor Kualitas */}
    <Card className="!p-4">
      <h3 className="text-xs font-bold uppercase tracking-widest text-neutral-text-secondary mb-3">
        Kelengkapan Laporan
      </h3>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium">{laporan.completeness_percentage || 0}%</span>
        <span className="hidden" aria-hidden="true">
          {(laporan.completeness_percentage || 0) >= 80 ? 'Baik' : 'Perlu dilengkapi'}
        </span>
        <span className="text-xs text-neutral-text-secondary">
          {(laporan.completeness_percentage || 0) >= 80 ? 'Baik' : 'Perlu dilengkapi'}
        </span>
      </div>
      <ProgressBar
        value={laporan.completeness_percentage || 0}
        color={(laporan.completeness_percentage || 0) >= 80 ? 'success' : 'warning'}
        size="sm"
      />
    </Card>
  </div>
  );
};

// ========== PASCA-RAPIM SIDEBAR ==========
const PascaRapimSidebar: React.FC<{
  laporan: any;
  activePpepp: any;
  isOverdue: boolean;
  isMenungguKonfirmasi: boolean;
  isTerhambat: boolean;
  targetEfektif: string | null;
  sumberTarget: string;
  isPic: boolean;
  canDecideHambatan: boolean;
  canConfirmSelesai: boolean;
}> = ({
  laporan,
  activePpepp,
  isOverdue,
  isMenungguKonfirmasi,
  isTerhambat,
  targetEfektif,
  sumberTarget,
  isPic,
  canDecideHambatan,
  canConfirmSelesai,
}) => {
  const progress = activePpepp?.progress_percentage || 0;
  const progressEntries = Array.isArray(activePpepp?.progress) ? activePpepp.progress.length : 0;
  const hasNoProgressYet = progress <= 0 && progressEntries === 0;
  const isNoProgressOverdue = hasNoProgressYet && isOverdue;
  const daysRemaining = targetEfektif
    ? differenceInDays(new Date(targetEfektif), new Date())
    : null;

  // Determine status tindak lanjut
  const getTindakLanjutStatus = () => {
    if (isTerhambat) return { label: 'Terhambat', color: 'danger', icon: Pause };
    if (isMenungguKonfirmasi) return { label: 'Menunggu Konfirmasi', color: 'warning', icon: Clock };
    if (progress >= 100) return { label: 'Selesai 100%', color: 'success', icon: CheckCircle };
    if (isNoProgressOverdue) return { label: 'Belum Ada Progress', color: 'danger', icon: AlertTriangle };
    if (hasNoProgressYet) return { label: 'Belum Ada Progress', color: 'warning', icon: Clock };
    if (progress >= 50) return { label: 'Berjalan Baik', color: 'info', icon: TrendingUp };
    return { label: 'Sedang Dikerjakan', color: 'teal', icon: Play };
  };

  const tlStatus = getTindakLanjutStatus();
  const TlIcon = tlStatus.icon;

  return (
    <div className="space-y-4">
      {/* Status Tindak Lanjut - PRIORITAS UTAMA */}
      <Card className="!p-0 overflow-hidden">
        {/* Header dengan gradient */}
        <div className={`p-4 bg-gradient-to-r ${isTerhambat ? 'from-red-500 to-rose-600' :
          isMenungguKonfirmasi ? 'from-amber-500 to-orange-500' :
            progress >= 100 ? 'from-emerald-500 to-teal-600' :
              isOverdue ? 'from-red-500 to-rose-600' :
                'from-teal-500 to-teal-600'
          } text-white`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wider opacity-80">Status Tindak Lanjut</span>
            <PhaseBadge phase="pasca_rapim" />
          </div>
          <div className="flex items-center gap-2">
            <TlIcon className="w-5 h-5" />
            <span className="text-lg font-black">{tlStatus.label}</span>
          </div>
        </div>

        {/* Progress */}
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-neutral-text-secondary">Progress</span>
            <span className="text-xl font-black text-teal-main">{progress}%</span>
          </div>
          <ProgressBar
            value={progress}
            color={isTerhambat ? 'danger' : progress >= 100 ? 'success' : 'teal'}
            size="md"
          />

          {/* Status Description */}
          <p className="mt-3 text-sm text-neutral-text-secondary">
            {isTerhambat
              ? isPic
                ? 'Anda telah melaporkan hambatan. Menunggu keputusan pimpinan untuk menentukan langkah selanjutnya.'
                : canDecideHambatan
                  ? 'PIC melaporkan hambatan. Buka detail tindak lanjut untuk meninjau kendala dan memilih tindakan yang diperlukan.'
                  : 'Tindak lanjut sedang mengalami hambatan dan menunggu keputusan pimpinan.'
              : isMenungguKonfirmasi
                ? canConfirmSelesai
                  ? 'Progress sudah 100%. Tinjau dan konfirmasi penyelesaian tindak lanjut ini.'
                  : isPic
                    ? 'Progress sudah 100%. Menunggu konfirmasi penyelesaian dari pimpinan.'
                    : 'Progress 100%. PIC menunggu konfirmasi penyelesaian dari pimpinan.'
                : isNoProgressOverdue
                  ? `Tindak lanjut sudah ditetapkan, tetapi belum ada progress yang dicatat dan target sudah lewat ${Math.abs(daysRemaining || 0)} hari.`
                : hasNoProgressYet
                  ? 'Tindak lanjut sudah ditetapkan, tetapi belum ada progress yang dicatat oleh PIC.'
                : isOverdue
                  ? `PIC sedang mengerjakan tindak lanjut, tetapi target sudah lewat ${Math.abs(daysRemaining || 0)} hari.`
                : `PIC sedang mengerjakan tindak lanjut.${daysRemaining !== null && daysRemaining > 0
                  ? ` Sisa waktu: ${daysRemaining} hari.`
                  : ''}`
            }
          </p>
        </div>
      </Card>

      {/* Info PIC & Target - DIGABUNG */}
      <Card className="!p-4">
        <h3 className="text-xs font-bold uppercase tracking-widest text-neutral-text-secondary mb-4">
          Penanggung Jawab
        </h3>

        {activePpepp?.pic_user ? (
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-400 to-teal-600 text-white flex items-center justify-center text-lg font-bold shrink-0">
              {activePpepp.pic_user.name?.charAt(0) || 'P'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-neutral-text-primary truncate">
                {activePpepp.pic_user.name}
              </p>
              <p className="text-xs text-neutral-text-secondary">
                {activePpepp.pic_unit?.name || '-'}
              </p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-neutral-text-secondary italic mb-4">PIC belum ditentukan</p>
        )}

        {/* Target & Deadline */}
        <div className="space-y-3 pt-3 border-t border-neutral-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CalendarCheck className={`w-4 h-4 ${isOverdue ? 'text-danger' : 'text-neutral-text-secondary'}`} />
              <span className="text-xs text-neutral-text-secondary">Target</span>
            </div>
            <div className="text-right">
              <p className={`text-sm font-semibold ${isOverdue ? 'text-danger' : ''}`}>
                {targetEfektif
                  ? new Date(targetEfektif).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })
                  : '-'
                }
              </p>
              {daysRemaining !== null && (
                <p className={`text-[10px] ${isOverdue ? 'text-danger font-bold' :
                  daysRemaining <= 7 ? 'text-amber-600' : 'text-neutral-text-secondary'
                  }`}>
                  {isOverdue
                    ? `Terlambat ${Math.abs(daysRemaining)} hari`
                    : `${daysRemaining} hari lagi`
                  }
                </p>
              )}
            </div>
          </div>

          {sumberTarget === 'keputusan_rapim' && (
            <div className="flex items-center gap-1.5 text-[10px] text-blue-600 bg-blue-50 px-2 py-1 rounded-lg">
              <Award className="w-3 h-3" />
              Target ditetapkan dalam RAPIM
            </div>
          )}
        </div>

        {/* CTA ke Tindak Lanjut */}
        {activePpepp?.id && (
          <Link to={`/tindak-lanjut/${activePpepp.id}`} className="block mt-4">
            <Button
              variant="primary"
              size="sm"
              className="w-full"
              icon={<ExternalLink className="w-4 h-4" />}
            >
              Buka Detail Tindak Lanjut
            </Button>
          </Link>
        )}

        {isTerhambat && (() => {
          if (isPic) {
            return (
              <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-3">
                <p className="text-xs font-bold uppercase tracking-widest text-amber-700">Hambatan Dilaporkan</p>
                <p className="mt-1 text-sm leading-relaxed text-amber-800">
                  Hambatan sudah dilaporkan. Menunggu keputusan pimpinan untuk menentukan langkah selanjutnya.
                </p>
              </div>
            );
          }

          if (canDecideHambatan) {
            return (
              <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-3">
                <p className="text-xs font-bold uppercase tracking-widest text-red-600">Perlu Keputusan Pimpinan</p>
                <p className="mt-1 text-sm leading-relaxed text-red-700">
                  Buka detail tindak lanjut untuk meninjau hambatan dan memilih langkah lanjutan yang diperlukan.
                </p>
              </div>
            );
          }

          return (
            <div className="mt-3 rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-3">
              <p className="text-xs font-bold uppercase tracking-widest text-neutral-600">Tindak Lanjut Terhambat</p>
              <p className="mt-1 text-sm leading-relaxed text-neutral-700">
                Tindak lanjut sedang mengalami hambatan dan menunggu keputusan pimpinan.
              </p>
            </div>
          );
        })()}
      </Card>

      <Card className="!p-4">
        <details className="group">
          <summary className="flex items-center justify-between cursor-pointer list-none">
            <div className="flex items-center gap-2">
              <Info className="w-4 h-4 text-blue-600" />
              <h3 className="text-xs font-bold uppercase tracking-widest text-neutral-text-secondary">
                Panduan Halaman Ini
              </h3>
            </div>
            <ChevronDown className="w-4 h-4 text-neutral-text-secondary group-open:rotate-180 transition-transform" />
          </summary>

          <div className="mt-3 pt-3 border-t border-neutral-border space-y-3">
            <p className="text-sm text-neutral-text-secondary leading-relaxed">
              Halaman ini menampilkan ringkasan status tindak lanjut, PIC yang bertugas, dan target waktu terbaru.
            </p>
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-3">
              <p className="text-sm text-blue-700 leading-relaxed">
                Jika Anda ingin melihat perkembangan kerja secara lengkap, membuka riwayat progres, atau memperbarui pekerjaan PIC, gunakan tombol <span className="font-semibold text-blue-900">Buka Detail Tindak Lanjut</span>.
              </p>
            </div>
            {isTerhambat && (
              <div className={`rounded-xl border p-3 ${
                isPic
                  ? 'border-amber-200 bg-amber-50'
                  : canDecideHambatan
                    ? 'border-red-200 bg-red-50'
                    : 'border-neutral-200 bg-neutral-50'
              }`}>
                <p className={`text-sm leading-relaxed ${
                  isPic
                    ? 'text-amber-800'
                    : canDecideHambatan
                      ? 'text-red-700'
                      : 'text-neutral-700'
                }`}>
                  {isPic
                    ? <>Jika status sedang <span className="font-semibold">Terhambat</span>, Anda cukup memantau keputusan pimpinan dan menindaklanjuti arahan berikutnya dari halaman detail tindak lanjut.</>
                    : canDecideHambatan
                      ? <>Jika status sedang <span className="font-semibold">Terhambat</span>, buka detail tindak lanjut untuk melihat kendala dan menentukan langkah lanjutannya.</>
                      : <>Jika status sedang <span className="font-semibold">Terhambat</span>, tindak lanjut sedang menunggu keputusan pimpinan.</>
                  }
                </p>
              </div>
            )}
            <p className="text-sm text-neutral-text-secondary leading-relaxed">
              Ringkasan keputusan RAPIM di bawah membantu Anda mengingat apa yang ditugaskan, sedangkan halaman detail tindak lanjut dipakai untuk pelaksanaan hariannya.
            </p>
          </div>
        </details>
      </Card>

      {/* Quick Summary Keputusan (collapsed) */}
      <Card className="!p-4">
        <details className="group">
          <summary className="flex items-center justify-between cursor-pointer list-none">
            <h3 className="text-xs font-bold uppercase tracking-widest text-neutral-text-secondary">
              Ringkasan Keputusan RAPIM
            </h3>
            <ChevronDown className="w-4 h-4 text-neutral-text-secondary group-open:rotate-180 transition-transform" />
          </summary>

          {activePpepp && (
            <div className="mt-3 pt-3 border-t border-neutral-border space-y-2">
              {[
                { label: 'Program', value: activePpepp.program },
                { label: 'Pengendalian', value: activePpepp.pengendalian },
              ].map((item) => (
                <div key={item.label}>
                  <p className="text-[10px] font-semibold text-neutral-text-secondary uppercase">{item.label}</p>
                  <p className="text-xs text-neutral-text-primary line-clamp-2">{item.value || '-'}</p>
                </div>
              ))}

              {activePpepp.budget > 0 && (
                <div className="flex items-center gap-2 pt-2 border-t border-neutral-border/50">
                  <span className="text-[10px] text-neutral-text-secondary">Anggaran:</span>
                  <span className="text-xs font-semibold text-teal-main">
                    {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(activePpepp.budget)}
                  </span>
                </div>
              )}
            </div>
          )}
        </details>
      </Card>

      {/* Info Pelapor (collapsed) */}
      <Card className="!p-4">
        <details>
          <summary className="flex items-center justify-between cursor-pointer list-none">
            <h3 className="text-xs font-bold uppercase tracking-widest text-neutral-text-secondary">
              Info Pelapor
            </h3>
            <ChevronDown className="w-4 h-4 text-neutral-text-secondary" />
          </summary>
          <div className="mt-3 pt-3 border-t border-neutral-border space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-neutral-text-secondary">Pelapor</span>
              <span className="font-medium">{laporan.user?.name || '-'}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-neutral-text-secondary">Unit</span>
              <span className="font-medium">{laporan.unit?.name || '-'}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-neutral-text-secondary">Dilaporkan</span>
              <span className="font-medium">
                {laporan.created_at
                  ? formatDistanceToNow(new Date(laporan.created_at), { addSuffix: true, locale: idLocale })
                  : '-'
                }
              </span>
            </div>
          </div>
        </details>
      </Card>
    </div>
  );
};

// ========== SELESAI SIDEBAR ==========
const SelesaiSidebar: React.FC<{
  laporan: any;
  activePpepp: any;
  hariTerlambatHistoris: number;
  tanggalSelesaiAktual: string | null;
  targetEfektif: string | null;
  resolvedStep?: {
    step_label: string | null;
    feedback: string | null;
    validator: { id: number; name: string } | null;
  } | null;
}> = ({ laporan, activePpepp, hariTerlambatHistoris, tanggalSelesaiAktual, targetEfektif, resolvedStep }) => {
  const tepatWaktu = hariTerlambatHistoris === 0;
  const statusLaporan = laporan.status === 'draft' ? 'draf' : laporan.status;
  const isResolvedLocal = statusLaporan === 'diselesaikan_lokal';
  const resolverName = laporan.resolver?.name || resolvedStep?.validator?.name || 'Pimpinan terkait';
  const resolutionLabel = formatResolutionLevel(laporan.resolution_level);

  // Calculate duration
  const durasiPenyelesaian = (() => {
    if (!laporan.created_at || !tanggalSelesaiAktual) return null;
    return differenceInDays(new Date(tanggalSelesaiAktual), new Date(laporan.created_at));
  })();

  return (
    <div className="space-y-4">
      {/* Summary Card */}
      <Card className="!p-0 overflow-hidden">
        <div className={`p-5 bg-gradient-to-r ${statusLaporan === 'dibatalkan' ? 'from-red-500 to-rose-600' :
          isResolvedLocal ? 'from-cyan-500 to-teal-600' :
            tepatWaktu ? 'from-emerald-500 to-teal-600' : 'from-amber-500 to-orange-500'
          } text-white`}>
          <div className="flex items-center gap-3 mb-3">
            {statusLaporan === 'dibatalkan' ? (
              <XCircle className="w-8 h-8" />
            ) : isResolvedLocal ? (
              <Shield className="w-8 h-8" />
            ) : tepatWaktu ? (
              <CheckCircle className="w-8 h-8" />
            ) : (
              <AlertTriangle className="w-8 h-8" />
            )}
            <div>
              <p className="text-xs uppercase tracking-wider opacity-80">Hasil Akhir</p>
              <p className="text-xl font-black">
                {statusLaporan === 'dibatalkan' ? 'Dibatalkan' :
                  isResolvedLocal ? 'Diselesaikan Internal' :
                  statusLaporan === 'diarsipkan' ? 'Diarsipkan' :
                    tepatWaktu ? 'Selesai Tepat Waktu' : `Selesai Terlambat`}
              </p>
            </div>
          </div>

          {statusLaporan !== 'dibatalkan' && !isResolvedLocal && (
            <div className="flex items-center gap-4 text-sm opacity-90">
              {durasiPenyelesaian !== null && (
                <span className="flex items-center gap-1">
                  <Timer className="w-4 h-4" />
                  {durasiPenyelesaian} hari
                </span>
              )}
              {!tepatWaktu && hariTerlambatHistoris > 0 && (
                <span className="flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" />
                  +{hariTerlambatHistoris} hari
                </span>
              )}
            </div>
          )}

          {isResolvedLocal && (
            <div className="space-y-1 text-sm opacity-90">
              <p>Laporan ditangani langsung pada tahap validasi dan tidak diteruskan ke eskalasi berikutnya.</p>
              <div className="flex flex-wrap items-center gap-3 text-xs font-semibold">
                <span className="inline-flex items-center gap-1">
                  <Flag className="w-3.5 h-3.5" />
                  Level: {resolutionLabel}
                </span>
                <span className="inline-flex items-center gap-1">
                  <UserCheck className="w-3.5 h-3.5" />
                  Oleh: {resolverName}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="p-4 grid grid-cols-2 gap-3">
          <div className="text-center p-3 bg-neutral-bg rounded-xl">
            <p className="text-2xl font-black text-teal-main">100%</p>
            <p className="text-[10px] text-neutral-text-secondary uppercase">Progress</p>
          </div>
          <div className="text-center p-3 bg-neutral-bg rounded-xl">
            <p className={`${isResolvedLocal ? 'text-lg' : 'text-2xl'} font-black text-neutral-text-primary`}>
              {isResolvedLocal ? resolutionLabel : (durasiPenyelesaian ?? '-')}
            </p>
            <p className="text-[10px] text-neutral-text-secondary uppercase">
              {isResolvedLocal ? 'Penyelesaian' : 'Hari'}
            </p>
          </div>
        </div>
      </Card>

      {isResolvedLocal && (
        <Card className="!p-4 border border-teal-200 bg-teal-50/40">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-teal-main text-white flex items-center justify-center shrink-0">
              <Shield className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h3 className="text-xs font-bold uppercase tracking-widest text-teal-700 mb-1">
                Penyelesaian Internal
              </h3>
              <p className="text-sm text-teal-900 leading-relaxed">
                Laporan ini selesai pada tahap validasi. Kasus tidak diteruskan ke tahap eskalasi berikutnya karena sudah diputuskan bisa ditangani langsung di internal.
              </p>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-xl border border-teal-100 bg-white/80 p-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-text-secondary">Diputuskan Oleh</p>
              <p className="mt-1 text-sm font-semibold text-neutral-text-primary">{resolverName}</p>
              <p className="text-xs text-neutral-text-secondary">{resolvedStep?.step_label || 'Tahap validasi aktif'}</p>
            </div>
            <div className="rounded-xl border border-teal-100 bg-white/80 p-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-text-secondary">Level Penyelesaian</p>
              <p className="mt-1 text-sm font-semibold text-neutral-text-primary">{resolutionLabel}</p>
              <p className="text-xs text-neutral-text-secondary">Tidak perlu dilanjutkan ke RAPIM.</p>
            </div>
          </div>

          {resolvedStep?.feedback && (
            <div className="mt-3 rounded-xl border border-teal-100 bg-white/90 p-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-text-secondary">Catatan Penyelesaian</p>
              <p className="mt-1 text-sm text-neutral-text-primary italic">"{resolvedStep.feedback}"</p>
            </div>
          )}
        </Card>
      )}

      {/* Timeline Summary */}
      <Card className="!p-4">
        <h3 className="text-xs font-bold uppercase tracking-widest text-neutral-text-secondary mb-4">
          Timeline
        </h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between text-xs">
            <span className="text-neutral-text-secondary">Dilaporkan</span>
            <span className="font-medium">
              {laporan.created_at
                ? new Date(laporan.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })
                : '-'
              }
            </span>
          </div>
          {targetEfektif && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-neutral-text-secondary">Target</span>
              <span className="font-medium">
                {new Date(targetEfektif).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
            </div>
          )}
          <div className="flex items-center justify-between text-xs">
            <span className="text-neutral-text-secondary">Diselesaikan</span>
            <span className={`font-medium ${!tepatWaktu ? 'text-amber-600' : 'text-emerald-600'}`}>
              {tanggalSelesaiAktual
                ? new Date(tanggalSelesaiAktual).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })
                : '-'
              }
            </span>
          </div>
        </div>
      </Card>

      {/* PIC yang menyelesaikan */}
      {activePpepp?.pic_user && (
        <Card className="!p-4">
          <h3 className="text-xs font-bold uppercase tracking-widest text-neutral-text-secondary mb-3">
            Diselesaikan Oleh
          </h3>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center font-bold">
              {activePpepp.pic_user.name?.charAt(0) || 'P'}
            </div>
            <div>
              <p className="text-sm font-semibold">{activePpepp.pic_user.name}</p>
              <p className="text-xs text-neutral-text-secondary">{activePpepp.pic_unit?.name || '-'}</p>
            </div>
          </div>
        </Card>
      )}

      {/* Historis (collapsed) */}
      <Card className="!p-4">
        <details>
          <summary className="flex items-center justify-between cursor-pointer list-none">
            <h3 className="text-xs font-bold uppercase tracking-widest text-neutral-text-secondary">
              Info Historis
            </h3>
            <ChevronDown className="w-4 h-4 text-neutral-text-secondary" />
          </summary>
          <div className="mt-3 pt-3 border-t border-neutral-border space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-neutral-text-secondary">Pelapor</span>
              <span className="font-medium">{laporan.user?.name || '-'}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-neutral-text-secondary">Unit</span>
              <span className="font-medium">{laporan.unit?.name || '-'}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-neutral-text-secondary">Risiko</span>
              <Badge variant="risk" risk={laporan.tingkat_risiko} size="sm" />
            </div>
          </div>
        </details>
      </Card>
    </div>
  );
};

// ========== MAIN COMPONENT ==========
const LaporanDetail: React.FC = () => {
  const { id } = useParams();
  const [laporan, setLaporan] = useState<any>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const [activeTab, setActiveTab] = useState<'detail' | 'komentar' | 'riwayat' | 'lampiran'>('detail');

  const roleInfo = useRole();
  const caps = useCapabilities();
  const workflowValidation = useWorkflowValidation(laporan?.id || null);
  const currentUser = useAuthStore((s) => s.user);
  const workflow = useWorkflow(laporan);
  const isFetchingRef = useRef(false);
  const refreshWorkflowValidation = workflowValidation.refresh;

  const fetchData = useCallback(async (options?: { silent?: boolean }) => {
    const silent = options?.silent ?? false;
    if (!id || isFetchingRef.current) return;

    try {
      isFetchingRef.current = true;
      if (!silent) {
        setLoading(true);
      }

      const data = await laporanService.getById(Number(id));
      const d = (data as any)?.data || data;
      setLaporan(d);
      setComments(d.comments || []);
      setAttachments(d.attachments || []);

      if (workflowValidation?.refresh) {
        await workflowValidation.refresh();
      }
    } catch (err) {
      console.error('Failed to load laporan:', err);
      if (!silent) {
        toast.error('Gagal memuat detail laporan');
      }
    } finally {
      isFetchingRef.current = false;
      if (!silent) {
        setLoading(false);
      }
    }
  }, [id, refreshWorkflowValidation]);

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id, fetchData]);

  useEffect(() => {
    if (!id) return;

    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        fetchData({ silent: true });
      }
    };

    const refreshOnFocus = () => {
      fetchData({ silent: true });
    };

    const interval = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchData({ silent: true });
      }
    }, 5000);

    document.addEventListener('visibilitychange', refreshWhenVisible);
    window.addEventListener('focus', refreshOnFocus);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      window.removeEventListener('focus', refreshOnFocus);
    };
  }, [id, fetchData]);

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    setSendingComment(true);
    try {
      await laporanService.addComment(Number(id), newComment);
      toast.success('Komentar berhasil ditambahkan');
      setNewComment('');
      await fetchData({ silent: true });
    } catch (err) {
      toast.error('Gagal mengirim komentar');
    } finally {
      setSendingComment(false);
    }
  };

  const handleStatusChange = async (toState: string, notes?: string) => {
    const result = await workflow.changeStatus(toState, notes);
    if (result) {
      await fetchData();
    }
    return !!result;
  };

  if (loading) return (
    <Loading variant="page" message="Memuat detail laporan..." />
  );

  if (!laporan) return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Laporan', href: '/laporan' }, { label: 'Tidak Ditemukan' }]} />
      <Card><p className="text-center py-8 text-sm text-neutral-text-secondary">Laporan tidak ditemukan</p></Card>
    </div>
  );

  // ========== COMPUTED VALUES ==========
  const statusLaporan = laporan.status === 'draft' ? 'draf' : laporan.status;
  const phase = getLaporanPhase(statusLaporan);

  const sortedPpepp = Array.isArray(laporan.ppepp_rekomendasi)
    ? [...laporan.ppepp_rekomendasi].sort((a: any, b: any) => (b?.id || 0) - (a?.id || 0))
    : [];

  const statusPpeppDinonaktifkan = ['dibatalkan', 'batal', 'cancelled'];
  const ppeppAktifUntukSelesai = sortedPpepp.filter((item: any) => {
    const status = String(item?.status || '').toLowerCase();
    return !statusPpeppDinonaktifkan.includes(status);
  });

  const activePpepp = ppeppAktifUntukSelesai.find((item: any) => item?.status && item.status !== 'selesai')
    || ppeppAktifUntukSelesai[0]
    || null;

  const latestPpepp = sortedPpepp[0] || null;
  const latestPpeppId = activePpepp?.id || latestPpepp?.id || null;
  const totalPpeppAktif = ppeppAktifUntukSelesai.length;

  const semuaPpeppAktifMenungguKonfirmasi = totalPpeppAktif > 0
    && ppeppAktifUntukSelesai.every((item: any) =>
      Number(item?.progress_percentage || 0) >= 100 && String(item?.status || '') === 'selesai'
    );

  const bolehTampilTombolSelesai = statusLaporan === 'berlangsung'
    && semuaPpeppAktifMenungguKonfirmasi
    && caps.has('confirm_completion');

  const targetEfektif = activePpepp?.target_date || laporan.target_selesai;
  const sumberTarget = activePpepp?.target_date ? 'keputusan_rapim' : 'sla_awal';
  const isLaporanBerlangsung = statusLaporan === 'berlangsung';
  const ppeppForStatus = activePpepp || latestPpepp;
  const statusSelesaiKonteks = ['selesai', 'diselesaikan_lokal'].includes(statusLaporan) || ppeppForStatus?.status === 'selesai';
  const tanggalSelesaiAktual = laporan.resolved_at || laporan.completed_at || ppeppForStatus?.updated_at || laporan.updated_at || null;
  const resolvedWorkflowSteps = workflowValidation.summary?.steps ?? [];
  const resolvedStep = resolvedWorkflowSteps.find((step: any) => step?.status === 'resolved') || null;
  const firstWorkflowStep = resolvedWorkflowSteps[0] ?? null;
  const currentWorkflowStep = workflowValidation.currentStep ?? firstWorkflowStep;
  const workflowActorLabel = laporan.warek_tujuan?.display_name
    || formatWorkflowActorLabel(currentWorkflowStep?.validator_role, currentWorkflowStep?.step_label);
  const isDirectWarekWorkflowRoute = phase === 'pra_rapim'
    && resolvedWorkflowSteps.length === 1
    && (
      String(currentWorkflowStep?.validator_role || '').startsWith('wakil_rektor_')
      || String(currentWorkflowStep?.step_label || '').toLowerCase().includes('wakil rektor')
    );
  const isDirectWarekFlow = isDirectWarekWorkflowRoute && statusLaporan === 'validasi_l1';
  const statusBadgeLabelOverride = isDirectWarekFlow
    ? `Menunggu Persetujuan ${workflowActorLabel}`
    : null;
  const nextActorLabelOverride = isDirectWarekFlow ? workflowActorLabel : null;

  const hariTerlambatHistoris = (() => {
    if (!statusSelesaiKonteks || !targetEfektif || !tanggalSelesaiAktual) return 0;
    const target = new Date(targetEfektif);
    const selesai = new Date(tanggalSelesaiAktual);
    target.setHours(0, 0, 0, 0);
    selesai.setHours(0, 0, 0, 0);
    if (selesai <= target) return 0;
    return Math.ceil((selesai.getTime() - target.getTime()) / (1000 * 60 * 60 * 24));
  })();

  const ppeppProgress = Number(ppeppForStatus?.progress_percentage || 0);

  const isMenungguKonfirmasi = isLaporanBerlangsung
    && (
      String(ppeppForStatus?.status || '') === 'selesai'
      || String(ppeppForStatus?.status || '') === 'validasi'
    )
    && ppeppProgress >= 100
    && statusLaporan !== 'selesai';

  const isTerhambat = isLaporanBerlangsung && ppeppForStatus?.status === 'terhambat';
  const isMenungguPenjadwalanRapimUlang = statusLaporan === 'antrean_rapim' && Boolean(laporan.dikembalikan_ke_rapim);

  const isOverdue = (() => {
    if (!targetEfektif || statusSelesaiKonteks) return false;
    const targetDate = new Date(targetEfektif);
    const today = new Date();
    targetDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return targetDate < today;
  })();

  const totalTransisi = (() => {
    if (!Array.isArray(workflow.history)) {
      return 0;
    }

    let displayCount = 0;

    for (let i = 0; i < workflow.history.length; i += 1) {
      const item = workflow.history[i];
      const nextItem = workflow.history[i + 1];
      const thirdItem = workflow.history[i + 2];

      const isDirectWarekToRapimSequence =
        item?.from_state === 'validasi_l2'
        && item?.to_state === 'antrean_rapim'
        && nextItem?.from_state === 'validasi_l1'
        && nextItem?.to_state === 'validasi_l2'
        && thirdItem?.from_state === 'step_1_pending'
        && thirdItem?.to_state === 'step_1_approved'
        && isSameHistoryMoment(item, nextItem)
        && isSameHistoryMoment(item, thirdItem);

      if (isDirectWarekToRapimSequence) {
        displayCount += 1;
        i += 2;
        continue;
      }

      displayCount += 1;
    }

    return displayCount;
  })();

  // ✅ FIX: pernahDitolakAtauRevisi HANYA untuk info historis
  // BUKAN untuk menentukan tombol aksi atau step aktif
  const pernahDitolakAtauRevisi = Boolean(
    Array.isArray(workflow.history) && workflow.history.some((item: any) =>
      ['ditolak_l1', 'ditolak_l2'].includes(item?.to_state)
      || (['draf', 'draft'].includes(item?.to_state) && ['diajukan', 'validasi_l1', 'validasi_l2'].includes(item?.from_state))
    )
  );

  // ✅ Tambahkan konstanta ini
  const REJECTED_STATUSES_LAPORAN = ['ditolak_l1', 'ditolak_l2'];
  const ACTIVE_PROCESS_STATUSES_LAPORAN = [
    'diajukan', 'validasi_l1', 'validasi_l2',
    'antrean_rapim', 'diputuskan', 'berlangsung',
    'selesai', 'diarsipkan', 'diselesaikan_lokal',
  ];
  // Apakah laporan ini sudah pernah ditolak lalu diajukan ulang?
  const sudahDiajukanUlangSetelahTolak = pernahDitolakAtauRevisi && 
    ACTIVE_PROCESS_STATUSES_LAPORAN.includes(statusLaporan);

  // ✅ FIX: Pisahkan: apakah SAAT INI dalam kondisi ditolak/revisi
  // vs pernah ditolak di masa lalu (historis)
  const sedangDitolakAktif = REJECTED_STATUSES_LAPORAN.includes(statusLaporan);

  const statusExplanationOverride = (() => {
    if (sudahDiajukanUlangSetelahTolak && statusLaporan === 'diajukan') {
      return 'Laporan ini telah diajukan ulang setelah revisi. Menunggu persetujuan dari validator.';
    }
    if (isDirectWarekFlow) {
      return `Laporan ini sedang menunggu persetujuan ${workflowActorLabel}. Untuk unit pelapor ini, proses persetujuan dilakukan langsung di tingkat Wakil Rektor.`;
    }
    return null;
  })();

  // Filter transitions
  const filteredTransitions = workflow.transitions.filter((t) => {
    if (t.to_state === 'selesai' && !bolehTampilTombolSelesai) return false;
    if (t.to_state === 'validasi_l2' && statusLaporan !== 'validasi_l1') return false;
    if (t.to_state === 'antrean_rapim' && !['validasi_l1', 'validasi_l2'].includes(statusLaporan)) return false;
    if (t.to_state === 'diputuskan') return false;
    if ((roleInfo.isAdmin || roleInfo.isRektor) && ['antrean_rapim', 'validasi_l1'].includes(t.to_state) && statusLaporan === 'validasi_l2') return false;
    const draftOnlyTransitions = ['diajukan', 'submitted'];
    if (draftOnlyTransitions.includes(t.to_state) && !['draf', 'draft', 'ditolak_l1', 'ditolak_l2'].includes(statusLaporan)) return false;
    if (t.to_state === 'diajukan' && !['draf', 'draft'].includes(statusLaporan)) return false;
    if (['wakil_rektor_1', 'wakil_rektor_2', 'wakil_rektor_3', 'wakil_rektor_4'].includes(roleInfo.role)
      && statusLaporan === 'validasi_l2' && ['antrean_rapim', 'ditolak_l2'].includes(t.to_state)) return false;
    return true;
  });

  const canEditStatus = statusLaporan === 'draf' ? 'draf' : (laporan.status || '');
  const canEditLaporanFinal = roleInfo.canEditLaporan(canEditStatus as any)
    && (roleInfo.isAdmin || Number(laporan.user_id || laporan.user?.id || 0) === Number(currentUser?.id || 0));

  const currentUserId = currentUser?.id;
  const currentUserRole = currentUser?.role?.name?.toLowerCase() || '';
  const isPicTindakLanjut = Number(activePpepp?.pic_user_id || activePpepp?.pic_user?.id || 0) === Number(currentUserId || 0);
  const canDecideHambatan = caps.canChangePic || caps.canExtendDeadline || caps.canReturnToRapim;
  const canConfirmSelesai = caps.canConfirmCompletion;

  // Cek: Apakah user ini sudah pernah validasi laporan ini?
  const userSudahValidasi = Array.isArray(laporan.validasi_logs) && laporan.validasi_logs.some(
    (log: any) => log.validator_id === currentUserId
  );

  // Cek: Apakah status laporan sudah melewati tahap validasi user ini?
  // ✅ FIX: Jika sudah diajukan ulang setelah ditolak, 
  // status historical tidak boleh membuat tombol dekan/warek tersembunyi
  // karena proses validasi dimulai dari awal lagi
  const dekanSudahApprove = sudahDiajukanUlangSetelahTolak
    ? false  // Setelah resubmit, dekan harus validasi ulang
    : ['validasi_l1', 'validasi_l2', 'antrean_rapim', 'diputuskan', 'berlangsung', 'selesai', 'diarsipkan', 'diselesaikan_lokal'].includes(statusLaporan);

  const warekSudahApprove = sudahDiajukanUlangSetelahTolak
    ? false  // Setelah resubmit, warek harus validasi ulang
    : ['validasi_l2', 'antrean_rapim', 'diputuskan', 'berlangsung', 'selesai', 'diarsipkan', 'diselesaikan_lokal'].includes(statusLaporan);

  // Dekan tidak boleh lihat tombol jika laporan sudah disetujui Dekan
  const isDekanDanSudahApprove = (currentUserRole === 'dekan' || currentUserRole === 'kalem') && dekanSudahApprove;

  // Warek tidak boleh lihat tombol jika laporan sudah disetujui Warek
  const isWarekDanSudahApprove = currentUserRole.startsWith('wakil_rektor_') && warekSudahApprove;

  // Sembunyikan tombol workflow jika user sudah tidak relevan
  // ✅ FIX: shouldShowWorkflowActions berdasarkan assignment aktif terbaru
  // BUKAN berdasarkan status historical
  // Jika laporan sudah diajukan ulang, cek assignment aktif dari summary
  const hasPendingAssignmentForCurrentUser = workflowValidation.summary?.steps?.some(
    (step: any) => step?.status === 'pending' && 
    (step?.validator?.id === currentUser?.id || workflowValidation.canApprove)
  );

  const shouldShowWorkflowActions = (() => {
    // Kalau sudah diajukan ulang setelah ditolak,
    // gunakan assignment aktif sebagai penentu (bukan historical approval)
    if (sudahDiajukanUlangSetelahTolak) {
      return !isDekanDanSudahApprove && !isWarekDanSudahApprove 
        && (workflowValidation.canApprove || workflowValidation.canReject || workflowValidation.canResolve);
    }
    return !isDekanDanSudahApprove && !isWarekDanSudahApprove;
  })();
  const praRapimActionPanel = (canEditLaporanFinal || shouldShowWorkflowActions) ? (
    <div className="space-y-3">
      {canEditLaporanFinal && (
        <Link to={`/laporan/${laporan.id}/edit`} className="block">
          <Button variant="outline" size="sm" className="w-full justify-center">
            Edit Laporan
          </Button>
        </Link>
      )}

      {shouldShowWorkflowActions && (
        <UnifiedWorkflowActions
          laporan={{
            id: laporan.id,
            kode: laporan.kode,
            judul: laporan.judul,
            status: statusLaporan,
            tingkat_risiko: laporan.tingkat_risiko,
            unit: laporan.unit,
            user: laporan.user,
          }}
          currentStep={workflowValidation.currentStep}
          canApprove={workflowValidation.canApprove && caps.canValidate}
          canReject={workflowValidation.canReject && caps.canValidate}
          canResolve={(() => {
            const activePendingStep = workflowValidation.summary?.steps?.find(
              (step: any) => step?.status === 'pending'
            );
            return workflowValidation.canResolve && caps.canValidate && (activePendingStep?.can_resolve_here === true);
          })()}
          transitions={filteredTransitions}
          processing={workflow.loading || workflowValidation.processing}
          layout="sidebar"
          onApprove={async (fb) => {
            const ok = await workflowValidation.approve(fb);
            if (ok) await fetchData();
            return ok;
          }}
          onReject={async (fb) => {
            const ok = await workflowValidation.reject(fb);
            if (ok) await fetchData();
            return ok;
          }}
          onResolve={async (fb) => {
            const ok = await workflowValidation.resolve(fb);
            if (ok) await fetchData();
            return ok;
          }}
          onTransition={handleStatusChange}
        />
      )}
    </div>
  ) : null;

  // ========== RENDER ==========
  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[
        { label: 'Dashboard', href: '/dashboard' },
        { label: 'Laporan', href: '/laporan' },
        { label: laporan.kode }
      ]} />

      {/* STATUS PROGRESS BAR - Hanya tampil di pra_rapim */}
      {phase === 'pra_rapim' && (
        <Card className="!p-5">
          {resolvedWorkflowSteps.length > 0 ? (
            <div className="space-y-3">
              <WorkflowStatusProgress
                steps={resolvedWorkflowSteps}
                currentStep={workflowValidation.summary?.current_step ?? null}
                resolution={workflowValidation.summary?.resolution ?? null}
                needsRapim={workflowValidation.summary?.needs_rapim ?? null}
                laporanStatus={statusLaporan}
              />
              {isDirectWarekFlow && (
                <div className="text-center">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                    {statusExplanationOverride || `Persetujuan dilakukan langsung oleh ${workflowActorLabel}`}
                  </span>
                </div>
              )}
              {!isDirectWarekFlow && statusExplanationOverride && (
                <div className="text-center">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                    {statusExplanationOverride}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <StatusProgress currentStatus={statusLaporan} validasiL1Skipped={laporan.validasi_l1_skipped} />
          )}
        </Card>
      )}

      {/* Dikembalikan ke RAPIM Alert */}
      {laporan.dikembalikan_ke_rapim && (
        <Card className="border-2 border-amber-300 bg-amber-50 !p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-amber-800">Laporan Dikembalikan ke RAPIM</p>
              <p className="text-sm text-amber-700 mt-1">
                Tindak lanjut mengalami hambatan. Menunggu penjadwalan rapat baru untuk pembahasan ulang.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* HEADER + ACTIONS */}
      <div className="flex flex-col gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <Link to="/laporan">
              <Button variant="ghost" size="sm" icon={<ArrowLeft className="w-4 h-4" />}>Kembali</Button>
            </Link>
            <span className="text-sm font-mono text-teal-main bg-teal-main/5 px-2 py-1 rounded">{laporan.kode}</span>
            <PhaseBadge phase={phase} />
          </div>
          <h1 className="text-xl font-black text-teal-dark tracking-tight">{laporan.judul}</h1>

          <div className="flex flex-wrap items-center gap-2 mt-3">
            {statusBadgeLabelOverride ? (
              <span
                className={`inline-flex items-center gap-1.5 font-semibold rounded-full whitespace-nowrap px-2 py-0.5 text-xs ${getStatusConfig(statusLaporan).bgColor} ${getStatusConfig(statusLaporan).textColor}`}
              >
                {getStatusConfig(statusLaporan).dotColor && (
                  <span className={`w-1.5 h-1.5 rounded-full ${getStatusConfig(statusLaporan).dotColor}`} />
                )}
                {statusBadgeLabelOverride}
              </span>
            ) : (
              <Badge variant="status" status={statusLaporan} size="sm" showDot />
            )}
            <Badge variant="risk" risk={laporan.tingkat_risiko} size="sm" />
            {statusLaporan === 'diselesaikan_lokal' && (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-teal-main/10 text-teal-main text-xs font-bold rounded-full">
                <Shield className="w-3 h-3" /> Level {formatResolutionLevel(laporan.resolution_level)}
              </span>
            )}
            {isOverdue && phase !== 'selesai' && (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-danger/10 text-danger text-xs font-bold rounded-full">
                <AlertTriangle className="w-3 h-3" /> Terlambat
              </span>
            )}
            {isMenungguKonfirmasi && (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-bold rounded-full">
                <Clock className="w-3 h-3" /> Menunggu Konfirmasi
              </span>
            )}
            {isTerhambat && (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded-full">
                <Pause className="w-3 h-3" /> Terhambat
              </span>
            )}
          </div>
        </div>
      </div>

      {/* CONTENT */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* MAIN CONTENT */}
        <div className="lg:col-span-2 space-y-6">
          {/* Tabs */}
          <div className="flex items-center gap-1 border-b border-neutral-border overflow-x-auto">
            {[
              { id: 'detail' as const, label: 'Detail', icon: <FileText className="w-4 h-4" /> },
              { id: 'komentar' as const, label: `Komentar (${comments.length})`, icon: <MessageSquare className="w-4 h-4" /> },
              { id: 'riwayat' as const, label: 'Riwayat', icon: <Clock className="w-4 h-4" /> },
              { id: 'lampiran' as const, label: `Lampiran (${attachments.length})`, icon: <Paperclip className="w-4 h-4" /> },
            ].map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === tab.id
                  ? 'border-teal-main text-teal-main'
                  : 'border-transparent text-neutral-text-secondary hover:text-neutral-text-primary'
                  }`}>
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>

          {/* TAB CONTENT - Detail */}
          {activeTab === 'detail' && (
            <div className="space-y-6">
              {/* Klasifikasi Laporan */}
              <Card className="!p-0 overflow-hidden">
                <div className="px-5 py-4 border-b border-blue-200 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                      <Tag className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-blue-900">Klasifikasi Laporan</h3>
                      <p className="text-xs text-blue-600">Kategori dan standar referensi</p>
                    </div>
                  </div>
                </div>

                <div className="p-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Kategori */}
                    <div className="p-4 bg-gradient-to-br from-neutral-bg to-white rounded-xl border border-neutral-border/50 hover:shadow-md transition-all">
                      <p className="text-[10px] font-bold text-neutral-text-secondary uppercase tracking-widest mb-2">
                        Kategori Masalah
                      </p>
                      {laporan.kategori_masalah ? (
                        <div className="flex items-center gap-2.5">
                          <div
                            className="w-4 h-4 rounded-full shadow-sm ring-2 ring-white"
                            style={{ backgroundColor: laporan.kategori_masalah.color }}
                          />
                          <span className="text-sm font-bold text-neutral-text-primary">
                            {laporan.kategori_masalah.nama}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-neutral-text-secondary italic flex items-center gap-1.5">
                          <AlertTriangle className="w-3.5 h-3.5" />
                          Belum ditentukan
                        </span>
                      )}
                    </div>

                    {/* Tingkat Risiko */}
                    <div className="p-4 bg-gradient-to-br from-neutral-bg to-white rounded-xl border border-neutral-border/50 hover:shadow-md transition-all">
                      <p className="text-[10px] font-bold text-neutral-text-secondary uppercase tracking-widest mb-2">
                        Tingkat Risiko
                      </p>
                      <Badge variant="risk" risk={laporan.tingkat_risiko} size="md" />
                    </div>

                    {/* Standar SPMI */}
                    <div className="p-4 bg-gradient-to-br from-neutral-bg to-white rounded-xl border border-neutral-border/50 hover:shadow-md transition-all">
                      <p className="text-[10px] font-bold text-neutral-text-secondary uppercase tracking-widest mb-2">
                        Standar SPMI
                      </p>
                      {laporan.standar_spmi ? (
                        <div>
                          <span className="text-xs font-mono text-teal-main bg-teal-main/5 px-1.5 py-0.5 rounded">
                            {laporan.standar_spmi.kode}
                          </span>
                          <p className="text-sm font-medium text-neutral-text-primary mt-1">
                            {laporan.standar_spmi.nama}
                          </p>
                        </div>
                      ) : (
                        <span className="text-sm text-neutral-text-secondary italic flex items-center gap-1.5">
                          <AlertTriangle className="w-3.5 h-3.5" />
                          Belum ditentukan
                        </span>
                      )}
                    </div>

                    {/* Indikator SPMI */}
                    <div className="p-4 bg-gradient-to-br from-neutral-bg to-white rounded-xl border border-neutral-border/50 hover:shadow-md transition-all">
                      <p className="text-[10px] font-bold text-neutral-text-secondary uppercase tracking-widest mb-2">
                        Indikator SPMI
                      </p>
                      {laporan.indikator_spmi ? (
                        <div>
                          <span className="text-xs font-mono text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">
                            {laporan.indikator_spmi.kode}
                          </span>
                          <p className="text-sm font-medium text-neutral-text-primary mt-1">
                            {laporan.indikator_spmi.nama}
                          </p>
                        </div>
                      ) : (
                        <span className="text-sm text-neutral-text-secondary italic flex items-center gap-1.5">
                          <AlertTriangle className="w-3.5 h-3.5" />
                          Belum ditentukan
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </Card>

              {/* Deskripsi Masalah */}
              <Card className="!p-0 overflow-hidden">
                <div className="px-5 py-4 border-b border-neutral-border bg-gradient-to-r from-neutral-bg to-white">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-neutral-text-primary">Deskripsi Masalah</h3>
                      <p className="text-xs text-neutral-text-secondary">Detail permasalahan yang dilaporkan</p>
                    </div>
                  </div>
                </div>
                <div className="p-5">
                  <p className="text-sm text-neutral-text-primary leading-relaxed whitespace-pre-wrap">
                    {laporan.deskripsi || <span className="italic text-neutral-text-secondary">Tidak ada deskripsi</span>}
                  </p>
                </div>
              </Card>

              {/* Dampak */}
              {laporan.dampak && (
                <Card className="!p-0 overflow-hidden">
                  <div className="px-5 py-4 border-b border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                        <AlertTriangle className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-amber-900">Dampak</h3>
                        <p className="text-xs text-amber-600">Konsekuensi dari permasalahan</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-5">
                    <p className="text-sm text-neutral-text-primary leading-relaxed whitespace-pre-wrap">
                      {laporan.dampak}
                    </p>
                  </div>
                </Card>
              )}

              {/* Akar Masalah */}
              {laporan.akar_masalah && (
                <Card className="!p-0 overflow-hidden">
                  <div className="px-5 py-4 border-b border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                        <Target className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-purple-900">Akar Masalah</h3>
                        <p className="text-xs text-purple-600">Root cause analysis</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-5">
                    <p className="text-sm text-neutral-text-primary leading-relaxed whitespace-pre-wrap">
                      {laporan.akar_masalah}
                    </p>
                  </div>
                </Card>
              )}

              {/* PPEPP Section */}
              {sortedPpepp.length > 0 && (
                <Card className="!p-0 overflow-hidden">
                  <div className="px-5 py-4 border-b border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                          <Award className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-emerald-900">Keputusan PPEPP</h3>
                          <p className="text-xs text-emerald-600">Hasil rapat pimpinan</p>
                        </div>
                      </div>
                      <span className="px-3 py-1 bg-emerald-100 text-emerald-700 text-xs font-bold rounded-full">
                        {sortedPpepp.length} keputusan
                      </span>
                    </div>
                  </div>

                  <div className="p-5 space-y-4">
                    {sortedPpepp.map((p: any, index: number) => {
                      const isPpeppAktif = p.id === latestPpeppId;
                      const progress = p.progress_percentage || 0;

                      return (
                        <div
                          key={p.id}
                          className={`
                  group relative overflow-hidden rounded-2xl border-2 transition-all duration-300
                  ${isPpeppAktif
                              ? 'bg-gradient-to-r from-teal-50 to-emerald-50 border-teal-300 shadow-md'
                              : 'bg-white border-neutral-border/50 hover:border-neutral-border hover:shadow-sm'
                            }
                `}
                        >
                          {/* Active Indicator */}
                          {isPpeppAktif && (
                            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-400 to-emerald-500" />
                          )}

                          <div className="p-4">
                            {/* Header */}
                            <div className="flex items-start justify-between gap-3 mb-4">
                              <div className="flex items-center gap-2 flex-wrap">
                                {isPpeppAktif && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-500 text-white text-[10px] font-bold rounded-full">
                                    <Zap className="w-3 h-3" />
                                    AKTIF
                                  </span>
                                )}
                                <Badge variant="status" status={p.status} size="sm" />
                                {p.priority && (
                                  <Badge variant="risk" risk={p.priority} size="sm" />
                                )}
                              </div>
                              <div className="text-right shrink-0">
                                <p className="text-[10px] text-neutral-text-secondary uppercase tracking-wider">Target</p>
                                <p className="text-xs font-semibold text-neutral-text-primary">
                                  {p.target_date
                                    ? new Date(p.target_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })
                                    : '-'
                                  }
                                </p>
                              </div>
                            </div>

                            {/* Progress Bar */}
                            <div className="mb-4">
                              <div className="flex items-center justify-between mb-1.5">
                                <span className="text-xs font-semibold text-neutral-text-secondary">Progress Tindak Lanjut</span>
                                <span className={`text-sm font-black ${progress >= 100 ? 'text-emerald-600' :
                                  progress >= 50 ? 'text-teal-main' :
                                    'text-amber-600'
                                  }`}>
                                  {progress}%
                                </span>
                              </div>
                              <div className="h-2 bg-neutral-bg rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all duration-500 ${progress >= 100 ? 'bg-gradient-to-r from-emerald-400 to-emerald-500' :
                                    progress >= 50 ? 'bg-gradient-to-r from-teal-400 to-teal-500' :
                                      'bg-gradient-to-r from-amber-400 to-amber-500'
                                    }`}
                                  style={{ width: `${Math.min(progress, 100)}%` }}
                                />
                              </div>
                            </div>

                            {/* PPEPP Info Grid */}
                            <div className="grid grid-cols-2 gap-3 mb-4">
                              <div className="p-3 bg-white/70 rounded-xl border border-neutral-border/30">
                                <p className="text-[10px] font-semibold text-neutral-text-secondary uppercase tracking-wider mb-1">
                                  Program
                                </p>
                                <p className="text-xs text-neutral-text-primary line-clamp-2 font-medium">
                                  {p.program || '-'}
                                </p>
                              </div>
                              <div className="p-3 bg-white/70 rounded-xl border border-neutral-border/30">
                                <p className="text-[10px] font-semibold text-neutral-text-secondary uppercase tracking-wider mb-1">
                                  PIC
                                </p>
                                <div className="flex items-center gap-2">
                                  {p.pic_user ? (
                                    <>
                                      <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-teal-400 to-teal-600 text-white flex items-center justify-center text-[10px] font-bold">
                                        {p.pic_user.name?.charAt(0) || 'P'}
                                      </div>
                                      <span className="text-xs font-medium text-neutral-text-primary truncate">
                                        {p.pic_user.name}
                                      </span>
                                    </>
                                  ) : (
                                    <span className="text-xs text-neutral-text-secondary italic">Belum ditentukan</span>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Action */}
                            <Link
                              to={`/tindak-lanjut/${p.id}`}
                              className={`
                      flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-xs font-bold transition-all
                      ${isPpeppAktif
                                  ? 'bg-gradient-to-r from-teal-500 to-teal-600 text-white hover:shadow-lg hover:shadow-teal-500/25'
                                  : 'bg-neutral-bg text-neutral-text-secondary hover:bg-neutral-border hover:text-neutral-text-primary'
                                }
                    `}
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                              Lihat Detail Tindak Lanjut
                            </Link>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              )}

              {/* No PPEPP Yet - untuk fase pra_rapim */}
              {sortedPpepp.length === 0 && phase !== 'pra_rapim' && (
                <Card className="!p-0 overflow-hidden">
                  <div className="px-5 py-4 border-b border-neutral-border bg-gradient-to-r from-neutral-bg to-white">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-neutral-bg flex items-center justify-center">
                        <Award className="w-5 h-5 text-neutral-text-secondary" />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-neutral-text-primary">Keputusan PPEPP</h3>
                        <p className="text-xs text-neutral-text-secondary">Hasil rapat pimpinan</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-center justify-center py-12 px-8 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-neutral-bg flex items-center justify-center mb-4">
                      <Award className="w-8 h-8 text-neutral-text-secondary opacity-40" />
                    </div>
                    <p className="text-sm font-semibold text-neutral-text-primary mb-1">Belum Ada Keputusan</p>
                    <p className="text-xs text-neutral-text-secondary max-w-sm">
                      Keputusan PPEPP akan dibuat setelah laporan dibahas dalam rapat pimpinan.
                    </p>
                  </div>
                </Card>
              )}
            </div>
          )}

          {/* TAB CONTENT - Comments */}
          {activeTab === 'komentar' && (
            <Card className="!p-0 overflow-hidden">
              {/* Header */}
              <div className="px-5 py-4 border-b border-neutral-border bg-gradient-to-r from-neutral-bg to-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center">
                      <MessageSquare className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-neutral-text-primary">Diskusi & Komentar</h3>
                      <p className="text-xs text-neutral-text-secondary">
                        {comments.length > 0
                          ? `${comments.length} komentar dalam laporan ini`
                          : 'Belum ada komentar'
                        }
                      </p>
                    </div>
                  </div>
                  {comments.length > 0 && (
                    <span className="px-3 py-1 bg-teal-main/10 text-teal-main text-xs font-bold rounded-full">
                      {comments.length}
                    </span>
                  )}
                </div>
              </div>

              {/* Comments List */}
              <div className="p-5">
                {comments.length > 0 ? (
                  <div className="space-y-4">
                    {comments.map((c: any, index: number) => {
                      const isCurrentUser = c.user?.id === currentUser?.id;
                      const isFirstComment = index === 0;

                      return (
                        <div
                          key={c.id}
                          className={`
                  group relative
                  ${c.parent_id ? 'ml-8 pl-4 border-l-2 border-neutral-border/50' : ''}
                  ${isFirstComment ? 'animate-in fade-in slide-in-from-top-2' : ''}
                `}
                        >
                          <div className={`
                  flex gap-3 p-4 rounded-2xl transition-all duration-200
                  ${isCurrentUser
                              ? 'bg-gradient-to-r from-teal-50 to-emerald-50 border border-teal-200/50'
                              : 'bg-neutral-bg/50 hover:bg-neutral-bg border border-transparent'
                            }
                `}>
                            {/* Avatar */}
                            <div className={`
                    w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold shrink-0
                    ${isCurrentUser
                                ? 'bg-gradient-to-br from-teal-400 to-teal-600 text-white'
                                : 'bg-gradient-to-br from-neutral-300 to-neutral-400 text-white'
                              }
                  `}>
                              {c.user?.name?.charAt(0)?.toUpperCase() || 'U'}
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              {/* Header */}
                              <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-bold text-neutral-text-primary">
                                    {c.user?.name || 'Unknown'}
                                  </span>
                                  {isCurrentUser && (
                                    <span className="text-[10px] px-1.5 py-0.5 bg-teal-main/10 text-teal-main rounded font-bold">
                                      Anda
                                    </span>
                                  )}
                                  {c.user?.role?.display_name && (
                                    <span className="text-[10px] px-2 py-0.5 bg-neutral-bg border border-neutral-border rounded-full text-neutral-text-secondary font-medium">
                                      {c.user.role.display_name}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  <span className="text-[10px] text-neutral-text-secondary">
                                    {c.created_at
                                      ? formatDistanceToNow(new Date(c.created_at), { addSuffix: true, locale: idLocale })
                                      : ''
                                    }
                                  </span>
                                </div>
                              </div>

                              {/* Message */}
                              <p className="text-sm text-neutral-text-primary leading-relaxed whitespace-pre-wrap">
                                {c.content}
                              </p>

                              {/* Timestamp detail on hover */}
                              {c.created_at && (
                                <p className="mt-2 text-[10px] text-neutral-text-secondary opacity-0 group-hover:opacity-100 transition-opacity">
                                  {new Date(c.created_at).toLocaleString('id-ID', {
                                    day: 'numeric',
                                    month: 'long',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  /* Empty State */
                  <div className="flex flex-col items-center justify-center py-12 px-8 text-center">
                    <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-neutral-bg to-white flex items-center justify-center mb-5 shadow-inner">
                      <MessageSquare className="w-10 h-10 text-neutral-text-secondary opacity-30" />
                    </div>
                    <h4 className="text-lg font-bold text-neutral-text-primary mb-2">Belum Ada Komentar</h4>
                    <p className="text-sm text-neutral-text-secondary max-w-sm">
                      Jadilah yang pertama memberikan komentar atau masukan untuk laporan ini.
                    </p>
                  </div>
                )}
              </div>

              {/* Add Comment Section */}
              <div className="px-5 py-4 border-t border-neutral-border bg-gradient-to-r from-white to-neutral-bg/50">
                <div className="flex gap-3">
                  {/* Current User Avatar */}
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-400 to-teal-600 text-white flex items-center justify-center text-sm font-bold shrink-0">
                    {currentUser?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>

                  {/* Input Area */}
                  <div className="flex-1">
                    <div className="relative">
                      <textarea
                        value={newComment}
                        onChange={e => setNewComment(e.target.value)}
                        placeholder="Tulis komentar atau masukan Anda..."
                        className={`
                w-full rounded-2xl border-2 px-4 py-3 pr-24 text-sm resize-none
                transition-all duration-200
                focus:outline-none focus:ring-0
                ${newComment.trim()
                            ? 'border-teal-main bg-white shadow-sm'
                            : 'border-neutral-border bg-neutral-bg/50 focus:border-teal-light focus:bg-white'
                          }
              `}
                        rows={3}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.ctrlKey && newComment.trim()) {
                            handleAddComment();
                          }
                        }}
                      />

                      {/* Send Button - Inside textarea */}
                      <div className="absolute bottom-3 right-3">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={handleAddComment}
                          loading={sendingComment}
                          disabled={!newComment.trim()}
                          icon={<Send className="w-4 h-4" />}
                          className={`
                  !rounded-xl transition-all duration-200
                  ${newComment.trim()
                              ? 'opacity-100 scale-100'
                              : 'opacity-50 scale-95'
                            }
                `}
                        >
                          Kirim
                        </Button>
                      </div>
                    </div>

                    {/* Helper Text */}
                    <div className="flex items-center justify-between mt-2 px-1">
                      <p className="text-[10px] text-neutral-text-secondary">
                        Tekan <kbd className="px-1.5 py-0.5 bg-neutral-bg border border-neutral-border rounded text-[9px] font-mono">Ctrl</kbd> + <kbd className="px-1.5 py-0.5 bg-neutral-bg border border-neutral-border rounded text-[9px] font-mono">Enter</kbd> untuk kirim
                      </p>
                      <p className="text-[10px] text-neutral-text-secondary">
                        {newComment.length > 0 && `${newComment.length} karakter`}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* TAB CONTENT - History */}
          {activeTab === 'riwayat' && (
            <Card>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-neutral-text-primary">Riwayat Alur Laporan</h3>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-neutral-bg border border-neutral-border">
                  {workflow.historyLoading ? 'Memuat...' : `${totalTransisi} transisi`}
                </span>
              </div>

              {/* Tambahkan ppeppData */}
              <WorkflowTimeline
                history={workflow.history}
                loading={workflow.historyLoading}
                ppeppData={activePpepp ? {
                  pic_user: activePpepp.pic_user,
                  pic_unit: activePpepp.pic_unit,
                  target_date: activePpepp.target_date,
                } : null}
                submitTargetLabel={isDirectWarekWorkflowRoute ? workflowActorLabel : null}
              />
            </Card>
          )}

          {/* TAB CONTENT - Attachments */}
          {activeTab === 'lampiran' && (
            <Card className="!p-0 overflow-hidden">
              {/* Header */}
              <div className="px-5 py-4 border-b border-neutral-border bg-gradient-to-r from-neutral-bg to-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center">
                      <Paperclip className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-neutral-text-primary">Dokumen Lampiran</h3>
                      <p className="text-xs text-neutral-text-secondary">
                        {attachments.length > 0
                          ? `${attachments.length} file terlampir`
                          : 'Tidak ada lampiran'
                        }
                      </p>
                    </div>
                  </div>
                  {attachments.length > 0 && (
                    <span className="px-3 py-1 bg-orange-100 text-orange-700 text-xs font-bold rounded-full">
                      {attachments.length} file
                    </span>
                  )}
                </div>
              </div>

              {/* Attachments List */}
              <div className="p-5">
                {attachments.length > 0 ? (
                  <div className="space-y-3">
                    {attachments.map((file: any, i: number) => {
                      // Determine file type icon & color
                      const getFileConfig = (filename: string, mimeType?: string) => {
                        const ext = filename?.split('.').pop()?.toLowerCase() || '';
                        const mime = mimeType?.toLowerCase() || '';

                        if (mime.includes('pdf') || ext === 'pdf') {
                          return { icon: '≡ƒôä', color: 'from-red-400 to-red-500', bg: 'bg-red-50', border: 'border-red-200' };
                        }
                        if (mime.includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
                          return { icon: '≡ƒû╝∩╕Å', color: 'from-purple-400 to-pink-500', bg: 'bg-purple-50', border: 'border-purple-200' };
                        }
                        if (mime.includes('spreadsheet') || mime.includes('excel') || ['xls', 'xlsx', 'csv'].includes(ext)) {
                          return { icon: '≡ƒôè', color: 'from-emerald-400 to-green-500', bg: 'bg-emerald-50', border: 'border-emerald-200' };
                        }
                        if (mime.includes('word') || mime.includes('document') || ['doc', 'docx'].includes(ext)) {
                          return { icon: '≡ƒô¥', color: 'from-blue-400 to-blue-500', bg: 'bg-blue-50', border: 'border-blue-200' };
                        }
                        if (mime.includes('presentation') || mime.includes('powerpoint') || ['ppt', 'pptx'].includes(ext)) {
                          return { icon: '≡ƒôæ', color: 'from-orange-400 to-amber-500', bg: 'bg-orange-50', border: 'border-orange-200' };
                        }
                        if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
                          return { icon: '≡ƒôª', color: 'from-amber-400 to-yellow-500', bg: 'bg-amber-50', border: 'border-amber-200' };
                        }
                        return { icon: '≡ƒôÄ', color: 'from-gray-400 to-gray-500', bg: 'bg-gray-50', border: 'border-gray-200' };
                      };
                      void getFileConfig;

                      const fileConfig = (() => {
                        const filename = file.original_name || file.filename || '';
                        const ext = filename.split('.').pop()?.toLowerCase() || '';
                        const mime = file.mime_type?.toLowerCase() || '';

                        if (mime.includes('pdf') || ext === 'pdf') {
                          return {
                            visualIcon: <FileText className="w-7 h-7 text-white" />,
                            color: 'from-red-400 to-red-500',
                            bg: 'bg-red-50',
                            border: 'border-red-200',
                          };
                        }
                        if (mime.includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
                          return {
                            visualIcon: <FileImage className="w-7 h-7 text-white" />,
                            color: 'from-purple-400 to-pink-500',
                            bg: 'bg-purple-50',
                            border: 'border-purple-200',
                          };
                        }
                        if (mime.includes('spreadsheet') || mime.includes('excel') || ['xls', 'xlsx', 'csv'].includes(ext)) {
                          return {
                            visualIcon: <FileSpreadsheet className="w-7 h-7 text-white" />,
                            color: 'from-emerald-400 to-green-500',
                            bg: 'bg-emerald-50',
                            border: 'border-emerald-200',
                          };
                        }
                        if (mime.includes('word') || mime.includes('document') || ['doc', 'docx'].includes(ext)) {
                          return {
                            visualIcon: <FileText className="w-7 h-7 text-white" />,
                            color: 'from-blue-400 to-blue-500',
                            bg: 'bg-blue-50',
                            border: 'border-blue-200',
                          };
                        }
                        if (mime.includes('presentation') || mime.includes('powerpoint') || ['ppt', 'pptx'].includes(ext)) {
                          return {
                            visualIcon: <Presentation className="w-7 h-7 text-white" />,
                            color: 'from-orange-400 to-amber-500',
                            bg: 'bg-orange-50',
                            border: 'border-orange-200',
                          };
                        }
                        if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
                          return {
                            visualIcon: <Archive className="w-7 h-7 text-white" />,
                            color: 'from-amber-400 to-yellow-500',
                            bg: 'bg-amber-50',
                            border: 'border-amber-200',
                          };
                        }

                        return {
                          visualIcon: <File className="w-7 h-7 text-white" />,
                          color: 'from-gray-400 to-gray-500',
                          bg: 'bg-gray-50',
                          border: 'border-gray-200',
                        };
                      })();
                      const fileSize = file.size
                        ? file.size >= 1024 * 1024
                          ? `${(file.size / 1024 / 1024).toFixed(1)} MB`
                          : `${(file.size / 1024).toFixed(0)} KB`
                        : 'Unknown size';
                      const uploadedAt = file.created_at
                        ? formatDistanceToNow(new Date(file.created_at), { addSuffix: true, locale: idLocale })
                        : null;

                      return (
                        <div
                          key={file.id || i}
                          className={`
                  group flex items-center gap-4 p-4 rounded-2xl border-2 transition-all duration-200
                  ${fileConfig.bg} ${fileConfig.border}
                  hover:shadow-md hover:scale-[1.01]
                `}
                        >
                          {/* File Icon */}
                          <div className={`
                  w-14 h-14 rounded-xl bg-gradient-to-br ${fileConfig.color} 
                  flex items-center justify-center shrink-0
                  shadow-sm group-hover:shadow-md transition-shadow
                `}>
                            {fileConfig.visualIcon}
                          </div>

                          {/* File Info */}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-neutral-text-primary truncate group-hover:text-teal-main transition-colors">
                              {file.original_name || file.filename || 'Unnamed file'}
                            </p>
                            <div className="hidden">
                              <span className="text-xs text-neutral-text-secondary">
                                {fileSize}
                              </span>
                              {file.uploader?.name && (
                                <>
                                  <span className="text-neutral-text-secondary">|</span>
                                  <span className="text-xs text-neutral-text-secondary">
                                    Oleh {file.uploader.name}
                                  </span>
                                </>
                              )}
                              {uploadedAt && (
                                <>
                                  <span className="text-neutral-text-secondary">|</span>
                                  <span className="text-xs text-neutral-text-secondary">
                                    {uploadedAt}
                                  </span>
                                </>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <span className="text-xs text-neutral-text-secondary">
                                {fileSize}
                              </span>
                              {file.uploader?.name && (
                                <>
                                  <span className="w-1 h-1 rounded-full bg-neutral-text-secondary/40" />
                                  <span className="text-xs text-neutral-text-secondary">
                                    Oleh {file.uploader.name}
                                  </span>
                                </>
                              )}
                              {uploadedAt && (
                                <>
                                  <span className="w-1 h-1 rounded-full bg-neutral-text-secondary/40" />
                                  <span className="text-xs text-neutral-text-secondary">
                                    {uploadedAt}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2 shrink-0">
                            {/* Preview (for images) */}
                            {file.mime_type?.includes('image') && file.url && (
                              <a
                                href={file.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2.5 rounded-xl bg-white/70 text-neutral-text-secondary hover:text-purple-600 hover:bg-purple-100 transition-all"
                                title="Lihat"
                              >
                                <Eye className="w-4 h-4" />
                              </a>
                            )}

                            {/* Download */}
                            <button
                              onClick={() => laporanService.downloadAttachment(
                                Number(id),
                                file.id,
                                file.original_name || file.filename
                              )}
                              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/70 text-neutral-text-secondary hover:text-teal-main hover:bg-teal-50 transition-all font-semibold text-xs"
                              title="Unduh"
                            >
                              <Download className="w-4 h-4" />
                              <span className="hidden sm:inline">Unduh</span>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  /* Empty State */
                  <div className="flex flex-col items-center justify-center py-16 px-8 text-center">
                    <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-neutral-bg to-white flex items-center justify-center mb-6 shadow-inner">
                      <Paperclip className="w-12 h-12 text-neutral-text-secondary opacity-30" />
                    </div>
                    <h4 className="text-lg font-bold text-neutral-text-primary mb-2">Tidak Ada Lampiran</h4>
                    <p className="text-sm text-neutral-text-secondary max-w-sm mb-6">
                      Laporan ini tidak memiliki dokumen pendukung yang dilampirkan.
                    </p>

                    {/* Info Box */}
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl max-w-md">
                      <div className="flex items-start gap-3">
                        <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                        <div className="text-left">
                          <p className="text-xs font-semibold text-blue-800 mb-1">Format yang didukung</p>
                          <p className="text-[11px] text-blue-600">
                            PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, JPG, PNG, ZIP (Maks. 10MB)
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Summary Footer (if has attachments) */}
              {attachments.length > 0 && (
                <div className="px-5 py-3 border-t border-neutral-border bg-neutral-bg/50">
                  <div className="flex items-center justify-between text-xs text-neutral-text-secondary">
                    <span>
                      Total: {attachments.length} file
                      {attachments.reduce((acc: number, f: any) => acc + (f.size || 0), 0) > 0 && (
                        <> ({(attachments.reduce((acc: number, f: any) => acc + (f.size || 0), 0) / 1024 / 1024).toFixed(1)} MB)</>
                      )}
                    </span>
                    <button
                      onClick={() => {
                        // Download all attachments
                        attachments.forEach((file: any) => {
                          laporanService.downloadAttachment(
                            Number(id),
                            file.id,
                            file.original_name || file.filename
                          );
                        });
                        toast.success(`Mengunduh ${attachments.length} file...`);
                      }}
                      className="flex items-center gap-1.5 text-teal-main hover:text-teal-dark font-semibold transition-colors"
                    >
                      <Download className="w-3.5 h-3.5" />
                      Unduh Semua
                    </button>
                  </div>
                </div>
              )}
            </Card>
          )}
        </div>

        {/* ========== CONTEXTUAL SIDEBAR ========== */}
        <div className="space-y-4">
          {phase === 'pra_rapim' && (
            <PraRapimSidebar
              laporan={laporan}
              statusLaporan={statusLaporan}
              workflowValidation={workflowValidation}
              pernahDitolakAtauRevisi={pernahDitolakAtauRevisi}
              isOverdue={isOverdue}
              statusBadgeLabelOverride={statusBadgeLabelOverride}
              statusExplanationOverride={statusExplanationOverride}
              nextActorLabelOverride={nextActorLabelOverride}
              actionPanel={praRapimActionPanel}
            />
          )}

          {phase === 'pasca_rapim' && (
            <PascaRapimSidebar
              laporan={laporan}
              activePpepp={activePpepp}
              isOverdue={isOverdue}
              isMenungguKonfirmasi={isMenungguKonfirmasi}
              isTerhambat={isTerhambat}
              targetEfektif={targetEfektif}
              sumberTarget={sumberTarget}
              isPic={isPicTindakLanjut}
              canDecideHambatan={canDecideHambatan}
              canConfirmSelesai={canConfirmSelesai}
            />
          )}

          {phase === 'selesai' && (
            <SelesaiSidebar
              laporan={laporan}
              activePpepp={activePpepp}
              hariTerlambatHistoris={hariTerlambatHistoris}
              tanggalSelesaiAktual={tanggalSelesaiAktual}
              targetEfektif={targetEfektif}
              resolvedStep={resolvedStep}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default LaporanDetail;
